import { useRef } from "react";
import { FilePlus2, Loader2, Play, UploadCloud } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "components/common/buttons/Button";
import { THEME_BRAND_SURFACE_CLASSES } from "providers/appearance/constants";
import { cn } from "utils/cn";
import { SCREENPLAY_EXTENSIONS } from "./StartProduction.utils";

export interface StartProductionProps {
  /** The page's main action (no production yet): the icon takes the brand color. */
  focal?: boolean;
  /** A file is being dragged over the window. */
  isDragging?: boolean;
  canCreateProject: boolean;
  onFile: (file: File) => void;
  onStartBlank: () => void;
  onExploreDemo?: () => void;
  hasDemo?: boolean;
  isDemoLoading?: boolean;
}

const tileClass =
  "group relative flex flex-col overflow-hidden rounded-xl border-2 border-dashed transition-all duration-200";

/** The footer actions, sized like the project card's next-stage Button. */
const actionClass = "gap-1.5 [&_svg]:h-3.5 [&_svg]:w-3.5";

/**
 * "Start a production" as the first tile of the projects grid, the same size as
 * a project card. Mirrors ProjectCard's layout: a 2:1 top, a meta row, a footer.
 */
export const StartProduction = ({
  focal = false,
  isDragging = false,
  canCreateProject,
  onFile,
  onStartBlank,
  onExploreDemo,
  hasDemo = false,
  isDemoLoading = false,
}: StartProductionProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  if (!canCreateProject) {
    return (
      <div className={cn(tileClass, "items-center justify-center border-border p-6 text-center")}>
        <p className="text-sm text-muted-foreground">
          Ask a workspace admin to create a project or give you access to one.
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: "easeOut" }}
      className={cn(
        tileClass,
        isDragging
          ? "border-primary bg-primary/[0.06]"
          : "border-border hover:border-foreground/25 hover:bg-muted/30"
      )}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={SCREENPLAY_EXTENSIONS.join(",")}
        data-testid="start-production-input"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) onFile(file);
        }}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="flex aspect-[2/1] w-full flex-col items-center justify-center gap-3 px-4 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50"
      >
        <span
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-xl transition-colors",
            focal || isDragging
              ? cn("border", THEME_BRAND_SURFACE_CLASSES)
              : "border border-border bg-muted/50 text-muted-foreground group-hover:text-foreground"
          )}
        >
          <UploadCloud className="h-5 w-5" aria-hidden />
        </span>
        <span className="flex flex-col gap-1">
          <span className="font-squada text-2xl uppercase leading-none text-foreground">
            {isDragging ? "Drop it here" : "Start a production"}
          </span>
          <span className="text-xs text-muted-foreground">
            Drop a screenplay anywhere, or <span className="underline">choose a file</span>
          </span>
        </span>
      </button>

      <span className="flex min-h-11 items-center px-4 py-3 text-xs text-muted-foreground/80">
        PDF, Final Draft, Fountain, or Celtx
      </span>

      <div className="mt-auto flex items-center gap-4 border-t border-dashed border-border px-4 py-2.5">
        <Button type="button" color="gray" size="xs" onClick={onStartBlank} className={actionClass}>
          <FilePlus2 aria-hidden />
          Start blank
        </Button>
        {onExploreDemo && (
          <Button
            type="button"
            color="gray"
            size="xs"
            onClick={onExploreDemo}
            disabled={isDemoLoading}
            className={cn(actionClass, "ml-auto")}
          >
            {isDemoLoading ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              <Play aria-hidden />
            )}
            {isDemoLoading ? "Preparing demo..." : hasDemo ? "Open the demo" : "Try a demo"}
          </Button>
        )}
      </div>
    </motion.div>
  );
};
